<template>
  <div class="admin-administradores">
    <DataTable
      title="Usuários Administrativos"
      subtitle="Gerencie os usuários administrativos do sistema"
      title-icon="fas fa-user-shield"
      :data="administradores"
      :columns="columns"
      :loading="loading"
      :custom-filters="filters"
      search-placeholder="Buscar por nome, e-mail ou CPF..."
      empty-title="Nenhum administrador encontrado"
      empty-message="Não há administradores cadastrados no sistema."
      empty-icon="fas fa-user-shield"
      :show-view="false"
      @edit="abrirEdicao"
      @delete="excluir"
    >
      <template #header-actions>
        <button class="btn btn-primary" @click="abrirCriacao">
          <i class="fas fa-plus"></i>
          Novo Administrador
        </button>
      </template>

      <template #cell-status="{ value }">
        <span class="status-badge" :class="getStatusClass(value)">
          {{ value === 'A' ? 'Ativo' : value === 'I' ? 'Inativo' : value }}
        </span>
      </template>
    </DataTable>

    <!-- Modal Criar/Editar -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="fecharModal">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas" :class="isEdit ? 'fa-edit' : 'fa-plus'"></i>
            {{ isEdit ? 'Editar Administrador' : 'Novo Administrador' }}
          </h4>
          <button @click="fecharModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="salvar">
            <div class="form-row">
              <div class="form-group">
                <label>Nome *</label>
                <input v-model.trim="form.nome" type="text" class="form-control" required />
              </div>
              <div class="form-group">
                <label>E-mail *</label>
                <input v-model.trim="form.email" type="email" class="form-control" required />
              </div>
            </div>

            <div class="form-row form-row-3">
              <div class="form-group">
                <label>CPF</label>
                <input v-model.trim="form.cpf" type="text" class="form-control" placeholder="000.000.000-00" />
              </div>
              <div class="form-group">
                <label>Perfil *</label>
                <select v-model="form.perfil" class="form-select" required>
                  <option value="Admin">Admin</option>
                  <option value="Gestor">Gestor</option>
                  <option value="Revisor">Revisor</option>
                  <option value="Auditor">Auditor</option>
                </select>
              </div>
              <div class="form-group">
                <label>Status *</label>
                <select v-model="form.status" class="form-select" required>
                  <option value="A">Ativo</option>
                  <option value="I">Inativo</option>
                </select>
              </div>
            </div>

            <div class="form-row" v-if="form.perfil === 'Auditor'">
              <div class="form-group">
                <label>Concursos permitidos (IDs CSV)</label>
                <textarea
                  v-model.trim="form.concursos_permitidos"
                  class="form-control"
                  rows="2"
                  placeholder="Ex: 12,15,20"
                />
                <small class="hint">Informe IDs de concursos separados por vírgula.</small>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Senha <span v-if="!isEdit">*</span></label>
                <input
                  v-model="form.senha"
                  type="password"
                  class="form-control"
                  :required="!isEdit"
                  minlength="6"
                  placeholder="Mínimo 6 caracteres"
                />
                <small v-if="isEdit" class="hint">Deixe em branco para manter a senha atual.</small>
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="fecharModal">
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <i v-if="saving" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-check"></i>
                {{ saving ? 'Salvando...' : 'Salvar' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import DataTable from '@/components/admin/DataTable.vue'

export default {
  name: 'AdminAdministradoresLista',
  components: {
    DataTable
  },
  data() {
    return {
      loading: false,
      saving: false,
      administradores: [],
      showFormModal: false,
      editando: null,
      form: {
        nome: '',
        email: '',
        cpf: '',
        perfil: 'Admin',
        status: 'A',
        concursos_permitidos: '',
        senha: ''
      },
      columns: [
        { key: 'nome', label: 'Nome', sortable: true },
        { key: 'email', label: 'E-mail', sortable: true },
        { key: 'cpf', label: 'CPF', sortable: true },
        { key: 'perfil', label: 'Perfil', sortable: true },
        { key: 'concursos_permitidos', label: 'Concursos (IDs)', sortable: false },
        { key: 'status', label: 'Status', sortable: true, type: 'badge' }
      ],
      filters: [
        {
          key: 'perfil',
          label: 'Perfil',
          placeholder: 'Todos os perfis',
          options: [
            { value: 'Admin', label: 'Admin' },
            { value: 'Gestor', label: 'Gestor' },
            { value: 'Revisor', label: 'Revisor' },
            { value: 'Auditor', label: 'Auditor' }
          ]
        },
        {
          key: 'status',
          label: 'Status',
          placeholder: 'Todos os status',
          options: [
            { value: 'A', label: 'Ativo' },
            { value: 'I', label: 'Inativo' }
          ]
        }
      ]
    }
  },
  computed: {
    isEdit() {
      return !!this.editando
    }
  },
  mounted() {
    this.carregar()
  },
  methods: {
    async carregar() {
      this.loading = true
      try {
        const { data } = await axios.get('/ps/admin/todos')
        this.administradores = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Erro ao carregar administradores:', error)
        this.$sweet.showError('Erro ao carregar administradores')
      } finally {
        this.loading = false
      }
    },
    abrirCriacao() {
      this.editando = null
      this.form = {
        nome: '',
        email: '',
        cpf: '',
        perfil: 'Admin',
        status: 'A',
        concursos_permitidos: '',
        senha: ''
      }
      this.showFormModal = true
    },
    abrirEdicao(admin) {
      this.editando = admin
      this.form = {
        nome: admin?.nome || '',
        email: admin?.email || '',
        cpf: admin?.cpf || '',
        perfil: admin?.perfil || 'Admin',
        status: admin?.status || 'A',
        concursos_permitidos: admin?.concursos_permitidos || '',
        senha: ''
      }
      this.showFormModal = true
    },
    fecharModal() {
      this.showFormModal = false
      this.editando = null
      this.form = {
        nome: '',
        email: '',
        cpf: '',
        perfil: 'Admin',
        status: 'A',
        concursos_permitidos: '',
        senha: ''
      }
    },
    async salvar() {
      if (!this.form.nome || !this.form.email) {
        this.$sweet.showError('Nome e e-mail são obrigatórios.')
        return
      }
      if (!this.isEdit && (!this.form.senha || this.form.senha.length < 6)) {
        this.$sweet.showError('Informe uma senha com no mínimo 6 caracteres.')
        return
      }

      this.saving = true
      try {
        const payload = {
          nome: this.form.nome,
          email: this.form.email,
          cpf: this.form.cpf || null,
          perfil: this.form.perfil,
          status: this.form.status,
          concursos_permitidos: this.form.perfil === 'Auditor' ? (this.form.concursos_permitidos || null) : null
        }
        if (this.form.senha) payload.senha = this.form.senha

        if (this.isEdit) {
          await axios.put(`/ps/admin/atualizar/${this.editando.id}`, payload)
          this.$sweet.showSuccess('Administrador atualizado com sucesso!')
        } else {
          await axios.post('/ps/admin/criar', payload)
          this.$sweet.showSuccess('Administrador criado com sucesso!')
        }

        this.fecharModal()
        await this.carregar()
      } catch (error) {
        console.error('Erro ao salvar administrador:', error)
        const msg = error.response?.data?.error || 'Erro ao salvar administrador'
        this.$sweet.showError(msg)
      } finally {
        this.saving = false
      }
    },
    async excluir(admin) {
      if (!admin?.id) return
      try {
        await axios.delete(`/ps/admin/excluir/${admin.id}`)
        this.$sweet.showSuccess('Administrador excluído com sucesso!')
        await this.carregar()
      } catch (error) {
        console.error('Erro ao excluir administrador:', error)
        const msg = error.response?.data?.error || 'Erro ao excluir administrador'
        this.$sweet.showError(msg)
      }
    },
    getStatusClass(status) {
      if (status === 'A') return 'status-ativo'
      if (status === 'I') return 'status-inativo'
      return 'status-default'
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-administradores {
  .status-badge {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;

    &.status-ativo {
      background: rgba(40, 167, 69, 0.1);
      color: #28a745;
    }

    &.status-inativo {
      background: rgba(220, 53, 69, 0.1);
      color: #dc3545;
    }

    &.status-default {
      background: rgba(108, 117, 125, 0.1);
      color: #6c757d;
    }
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 15px;
  width: 100%;
  max-width: 780px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  animation: slideInUp 0.25s ease-out;
}

.modal-header {
  padding: 20px 25px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .modal-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #667eea;
    }
  }

  .modal-close {
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 5px;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease;

    &:hover {
      background: #f8f9fa;
    }
  }
}

.modal-body {
  padding: 25px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 15px;

  &.form-row-3 {
    grid-template-columns: 1fr 1fr 1fr;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.form-group {
  label {
    display: block;
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
    font-size: 0.95rem;
  }
  .hint {
    display: block;
    margin-top: 6px;
    color: #6c757d;
    font-size: 12px;
  }
}

.modal-footer {
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
